import React from "react";
import toast from "react-hot-toast";
import { InverntroyService } from "../../../../../services/api/inventroy.service";
import UpdateSellingPriceModal from "../../../../../shared/UpdateSellingPriceModal";
import { Ban, CircleCheck, Edit } from "lucide-react";
import defaultAvatar from "../../../../../assets/images/default_avatar.jpg";
import UpdatePaymentStatusModal from "./UpdatePaymentStatusModal";

interface InvoiceDetailsProps {
  invoiceNumber: string | null;
  onBack?: () => void; // Added onBack prop for handling back navigation
  clinicId?: string;
  onRefresh?: () => void | Promise<any>;
}

interface ProductMedia {
  id: number;
  isMain: boolean;
  product_image_path: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  category_id: number;
  categoryName: string;
  is_published: boolean;
  is_rx_required: boolean;
  created_at: string;
  updated_at: string;
  tablet_per_stripe: number;
  newCgst: string;
  newSgst: string;
  newMrp: string;
  newSellingPrice: string;
}

interface Clinic {
  id: string;
  name: string;
  mobileNumber: string;
  registrationNumber: string;
  drugLicenseNumber: string;
  address: string;
  profileImage: string;
  supervisorName: string;
  avgRating: number;
  city: string;
  state: string;
  pincode: string;
  username: string;
  password: string;
}

interface Manufacturer {
  id: string;
  manufacturer_name: string;
  created_at: string;
  updated_at: string;
}

interface InvoiceItem {
  id: string;
  wholesaler_id: string;
  clinic_id: string;
  product_id: string;
  invoice_number: string;
  invoice_date: string;
  invoice_amount: string;
  payment_status?: string;
  paymentStatus?: string;
  inward_stock_id?: string;
  manufacturer_id: string;
  expiry_date: string;
  batch_number: string;
  hsn_number: string;
  no_of_items: number;
  is_sp_updated: boolean;
  mrp: string;
  free_items: number;
  product_rate: string;
  discount: string;
  cgst: string;
  sgst: string;
  created_at: string;
  updated_at: string;
  landing_cost: string;
  remaining_stocks: number;
  manufacturer: Manufacturer;
  clinic: Clinic;
  product: Product;
  productMedia: ProductMedia[];
  inwardStockQuantity: InwardStockQuantity;
}

interface InwardStockQuantity {
  id: number;
  inward_stock_id: string;
  no_of_items: number;
  free_items: number;
  created_at: string;
  updated_at: string;
}

const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({
  invoiceNumber,
  onBack,
  clinicId,
  onRefresh,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [invoiceData, setInvoiceData] = React.useState<InvoiceItem[]>([]);
  const [showModal, setShowModal] = React.useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<InvoiceItem | null>(
    null
  );

  const handlePaymentStatusUpdated = async (newStatus: string) => {
    setInvoiceData((prev) =>
      prev.map((item) => ({
        ...item,
        payment_status: newStatus,
        paymentStatus: newStatus,
      }))
    );
    await loadInvoiceData();
    if (onRefresh) {
      await onRefresh();
    } else {
      if (clinicId) {
        await InverntroyService.getInvoiceHistoryForClinic(1, 10, clinicId);
      } else {
        await InverntroyService.getInvoiceHistory(1, 10);
      }
    }
  };

  const loadInvoiceData = async () => {
    try {
      setIsLoading(true);
      const response = await InverntroyService.getInvoiceHistoryDetails(
        invoiceNumber as string
      );
      if (response.data.length > 0) {
        setInvoiceData(response.data);
      } else {
        toast.error(response.message);
        setInvoiceData([]);
      }
    } catch (error) {
      console.error("Error loading invoice data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (invoiceNumber) {
      loadInvoiceData();
    }
  }, [invoiceNumber]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount: string | number) => {
    const parsedToNumber = Number(amount).toFixed(2);
    return `₹ ${parsedToNumber.toLocaleString()}`;
  };

  const handleUpdateSellingPrice = (product: InvoiceItem) => {
    setSelectedItem(product);
    setShowModal(true);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Default back behavior (e.g., browser back or navigation)
      window.history.back();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!invoiceData.length) {
    return (
      <div className="p-6 text-center text-gray-500">No invoice data found</div>
    );
  }

  const firstItem = invoiceData[0];
  const totalProducts = invoiceData.reduce(
    (sum, item) => sum + item.no_of_items,
    0
  );

  return (
    <div className="">
      {showModal ? (
        <UpdateSellingPriceModal
          id={selectedItem?.id}
          productID={selectedItem?.product_id}
          onClose={() => setShowModal(false)}
        />
      ) : (
        <>
          {/* Back Button and Title */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={handleBack}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 className="text-lg font-medium text-gray-900">
              Invoice History
            </h2>
          </div>
          {/* Header Section with Back Button */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2">
            <div className="p-6 border-b border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-sm">
                <div>
                  <span className="text-gray-500 block mb-1">
                    Invoice Number
                  </span>
                  <span className="font-medium text-gray-900">
                    {firstItem.invoice_number}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Invoice Date</span>
                  <span className="font-medium text-gray-900">
                    {formatDate(firstItem.invoice_date)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">
                    Invoice Amount
                  </span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(firstItem.invoice_amount)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">
                    Payment Status
                  </span>
                  {(() => {
                    const status = (
                      firstItem.payment_status ||
                      (firstItem as any).paymentStatus ||
                      "pending"
                    ).toLowerCase();
                    const isCompleted =
                      status === "completed" || status === "success";
                    return (
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full capitalize ${
                            isCompleted
                              ? "bg-green-100 text-green-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {firstItem.payment_status ||
                            (firstItem as any).paymentStatus ||
                            "Pending"}
                        </span>
                        <button
                          type="button"
                          onClick={() => setIsPaymentModalOpen(true)}
                          className="p-1 text-gray-400 hover:text-[#199C4D] hover:bg-emerald-50 rounded-md transition-colors"
                          title="Edit Payment Status"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">Clinic Name</span>
                  <div className="flex items-center gap-2">
                    {firstItem.clinic?.profileImage ? (
                      <img
                        src={firstItem.clinic.profileImage}
                        alt={firstItem.clinic.name}
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "/placeholder-clinic.png";
                        }}
                      />
                    ) : (
                      <img src={defaultAvatar} alt="User" className="w-6 h-6" />
                    )}
                    <span className="font-medium text-gray-900">
                      {firstItem.clinic?.name || "N/A"}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1">
                    No.of Product
                  </span>
                  <span className="font-medium text-gray-900">
                    {totalProducts} pcs
                  </span>
                </div>
              </div>
            </div>

            {/* Product Details Section */}
            <div className="p-6">
              <h3 className="text-green-600 font-medium mb-4">
                PRODUCT DETAILS
              </h3>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="pb-3 font-medium">S.No.</th>
                      <th className="pb-3 font-medium">Product Name</th>
                      <th className="pb-3 font-medium">Quantity</th>
                      <th className="pb-3 font-medium">MRP</th>
                      <th className="pb-3 font-medium">New MRP</th>
                      <th className="pb-3 font-medium">Rate</th>
                      <th className="pb-3 font-medium">Expiry Date</th>
                      <th className="pb-3 font-medium">CGST</th>
                      <th className="pb-3 font-medium">SGST</th>
                      <th className="pb-3 font-medium">Landing Cost</th>
                      <th className="pb-3 font-medium">SP updated</th>
                      <th className="pb-3 font-medium">New SP</th>
                      <th className="pb-3 font-medium">Batch No.</th>
                      {!clinicId && (
                        <th className="pb-3 font-medium">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceData.map((item, index) => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-100 text-sm"
                      >
                        <td className="py-4 text-gray-500 font-medium">{index + 1}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                              {item.productMedia.length > 0 ? (
                                <img
                                  src={
                                    item.productMedia.find(
                                      (media) => media.isMain
                                    )?.product_image_path ||
                                    item.productMedia[0].product_image_path
                                  }
                                  alt={item.product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src =
                                      "/placeholder-product.png";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {item.product.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {item.product.id.slice(-7)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.inwardStockQuantity?(item.inwardStockQuantity?.no_of_items + item.inwardStockQuantity?.free_items):item.remaining_stocks} 
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.mrp}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {Number(item?.product?.newMrp).toFixed(2)}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {Number(item.product_rate).toFixed(2)}
                        </td>

                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.expiry_date
                            ? (() => {
                                const date = new Date(item.expiry_date);
                                const month = String(
                                  date.getMonth() + 1
                                ).padStart(2, "0"); // Months are 0-indexed
                                const year = String(date.getFullYear()).slice(
                                  -2
                                ); // Get last 2 digits of year
                                return `${month}/${year}`;
                              })()
                            : "N/A"}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.product.newCgst || item.cgst}%
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.product.newSgst || item.sgst}%
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {Number(item.landing_cost).toFixed(2)}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item?.is_sp_updated === true ? (
                            <CircleCheck className="w-5 h-5 bg-[#199C4D] rounded-full text-[white]" />
                          ) : item?.is_sp_updated === false ? (
                            <Ban className="w-5 h-5 bg-[#c62a2a] rounded-full text-[white]" />
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {Number(item?.product?.newSellingPrice).toFixed(2)}
                        </td>
                        <td className="py-4 text-gray-900 items-center justify-center">
                          {item.batch_number}
                        </td>
                        {!clinicId && (
                          <td className="py-4">
                            <button
                              onClick={() => handleUpdateSellingPrice(item)}
                              className="px-3 py-2 text-sm text-green-600 border border-[#199C4D] rounded-md transition-colors font-medium"
                            >
                              Update Selling Price
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      {isPaymentModalOpen && (
        <UpdatePaymentStatusModal
          inwardStockId={
            (firstItem as any)?.inward_stock_id ||
            firstItem?.id ||
            (firstItem as any)?.inwardStockQuantity?.inward_stock_id ||
            ""
          }
          invoiceNumber={firstItem.invoice_number}
          currentStatus={
            firstItem.payment_status ||
            (firstItem as any).paymentStatus ||
            "pending"
          }
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={handlePaymentStatusUpdated}
        />
      )}
    </div>
  );
};

export default InvoiceDetails;
